import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, Download } from 'lucide-react';

export default function AgingReportModal({
  isOpen,
  onClose,
  agingReportData,
  onDownloadPdf
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Invoice Aging Report
          </DialogTitle>
          <DialogDescription>
            Outstanding invoices categorized by age as of {new Date().toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto py-4">
          {agingReportData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-emerald-600">Current (0-30 days)</p>
                    <p className="text-lg font-bold text-emerald-700">{formatCurrency(agingReportData.aging_buckets.current.total)}</p>
                    <p className="text-xs text-emerald-600">{agingReportData.aging_buckets.current.count} invoice(s)</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-amber-600">31-60 days</p>
                    <p className="text-lg font-bold text-amber-700">{formatCurrency(agingReportData.aging_buckets['30_60'].total)}</p>
                    <p className="text-xs text-amber-600">{agingReportData.aging_buckets['30_60'].count} invoice(s)</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-orange-600">61-90 days</p>
                    <p className="text-lg font-bold text-orange-700">{formatCurrency(agingReportData.aging_buckets['60_90'].total)}</p>
                    <p className="text-xs text-orange-600">{agingReportData.aging_buckets['60_90'].count} invoice(s)</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-red-600">Over 90 days</p>
                    <p className="text-lg font-bold text-red-700">{formatCurrency(agingReportData.aging_buckets.over_90.total)}</p>
                    <p className="text-xs text-red-600">{agingReportData.aging_buckets.over_90.count} invoice(s)</p>
                  </CardContent>
                </Card>
              </div>

              {/* Summary Stats */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600">Total Outstanding</p>
                    <p className="text-2xl font-bold">{formatCurrency(agingReportData.summary.total_outstanding)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Average Days Outstanding</p>
                    <p className="text-2xl font-bold">{agingReportData.summary.average_days_outstanding} days</p>
                  </div>
                </div>
              </div>

              {/* Detailed List */}
              {Object.entries(agingReportData.aging_buckets).map(([key, bucket]) => (
                bucket.invoices.length > 0 && (
                  <div key={key} className="space-y-2">
                    <h4 className="font-semibold text-slate-700">{bucket.days} Days ({bucket.count} invoices)</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bucket.invoices.map(inv => (
                          <TableRow key={inv.id}>
                            <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                            <TableCell>{inv.client_name}</TableCell>
                            <TableCell className="font-mono text-sm">{inv.due_date}</TableCell>
                            <TableCell className="text-right">{inv.days_overdue}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(inv.balance_due)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">Loading aging report...</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={onDownloadPdf} className="bg-purple-600 hover:bg-purple-700">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
